import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions, isDevMode } from '@/lib/firebase'

export interface AppNotification {
  id: string
  type: 'market_price' | 'investment_update' | 'match_found' | 'contract_signed' | 'escrow_funded'
      | 'delivery_confirmed' | 'kyc_approved' | 'kyc_rejected' | 'payment_received'
      | 'report_submitted' | 'invoice_issued' | 'system'
  title: string
  body: string
  read: boolean
  data: Record<string, string>
  createdAt: string
}

interface NotificationsResult {
  notifications: AppNotification[]
  unreadCount: number
}

const EMPTY: NotificationsResult = { notifications: [], unreadCount: 0 }

export function useNotifications(limit = 50) {
  const qc = useQueryClient()

  // Immediately refresh when a foreground FCM message arrives
  useEffect(() => {
    const handler = () => void qc.invalidateQueries({ queryKey: ['notifications'] })
    window.addEventListener('mombongo:notification', handler)
    return () => window.removeEventListener('mombongo:notification', handler)
  }, [qc])

  return useQuery<NotificationsResult>({
    queryKey: ['notifications', limit],
    queryFn: async () => {
      if (isDevMode()) return EMPTY
      const fn = httpsCallable<{ limit: number }, NotificationsResult>(
        functions, 'getMyNotifications'
      )
      return (await fn({ limit })).data
    },
    staleTime: 10_000,
    refetchInterval: 15_000,
  })
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const fn = httpsCallable<{ notificationIds: string[] }, { ok: boolean }>(
        functions, 'markNotificationsRead'
      )
      await fn({ notificationIds })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
}
